import { validatePassword } from '@/utils/validators';

interface PasswordStrengthIndicatorProps {
    password: string;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
    const validation = validatePassword(password);

    const requirements: { key: keyof ReturnType<typeof validatePassword>, label: string }[] = [
        { key: 'hasMinLength', label: 'At least 8 characters' },
        { key: 'hasUppercase', label: 'Contains uppercase letter' },
        { key: 'hasLowercase', label: 'Contains lowercase letter' },
        { key: 'hasNumber', label: 'Contains a number' },
    ];

    // Cast key to keyof PasswordValidation for type safety
    const passedCount = requirements.filter(r => validation[r.key as keyof typeof validation]).length;
    const percentage = (passedCount / requirements.length) * 100;

    const getColor = () => {
        if (percentage <= 25) return 'danger';
        if (percentage <= 50) return 'warning';
        if (percentage <= 75) return 'info';
        return 'success';
    };

    const getLabel = () => {
        if (percentage <= 25) return 'Weak';
        if (percentage <= 50) return 'Fair';
        if (percentage <= 75) return 'Good';
        return 'Strong';
    };

    if (!password) return null;

    return (
        <div className="mt-2">
            <div className="d-flex align-items-center mb-2">
                <div className="progress flex-grow-1 me-2" style={{ height: 6 }}>
                    <div
                        className={`progress-bar bg-${getColor()}`}
                        style={{ width: `${percentage}%`, transition: 'width 0.3s ease' }}
                    />
                </div>
                <small className={`text-${getColor()} fw-bold`} style={{ fontSize: '0.75rem' }}>{getLabel()}</small>
            </div>

            <ul className="list-unstyled small mb-0">
                {requirements.map(req => (
                    <li key={req.key} className={validation[req.key as keyof typeof validation] ? 'text-success' : 'text-muted'}>
                        <i className={`bi ${validation[req.key as keyof typeof validation] ? 'bi-check-circle-fill' : 'bi-circle'} me-1`}></i>
                        {req.label}
                    </li>
                ))}
            </ul>
        </div>
    );
}
